import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit, Save, X, Building2, MapPin, XCircle } from 'lucide-react';
import AddItemModal from '../../components/AddItemModal';
import { apiClient } from '../../config/api';

const AdminGroupsPage = () => {
    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editedGroup, setEditedGroup] = useState(null);
    const [locationCounts, setLocationCounts] = useState({});
    const [userRole, setUserRole] = useState(null);
    
    // Location management state
    const [expandedGroupId, setExpandedGroupId] = useState(null);
    const [locations, setLocations] = useState([]);
    const [showLocationModal, setShowLocationModal] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);
    const [locationFormData, setLocationFormData] = useState({
        location_name: '',
        address: '',
    });
    const [availableBrands, setAvailableBrands] = useState([]);
    const [selectedBrandIds, setSelectedBrandIds] = useState([]);
    const [availableOEMs, setAvailableOEMs] = useState([]);
    const [error, setError] = useState('');

    useEffect(() => {
        loadGroups();
        loadLocationCounts();
        fetchBrands();
        fetchOEMs();
        fetchUserRole();
    }, []);

    const fetchUserRole = async () => {
        try {
            const data = await apiClient.get('/auth/me');
            setUserRole(data.user?.role || null);
        } catch (error) {
            console.error('Error fetching user role:', error);
        }
    };

    const fetchBrands = async () => {
        try {
            const data = await apiClient.get('/brands');
            setAvailableBrands(data || []);
        } catch (error) {
            console.error('Error fetching brands:', error);
        }
    };

    const fetchOEMs = async () => {
        try {
            const data = await apiClient.get('/oems');
            setAvailableOEMs((data || []).filter(o => o.is_active !== false));
        } catch (error) {
            console.error('Error fetching OEMs:', error);
        }
    };

    const loadGroups = async () => {
        try {
            setLoading(true);
            const data = await apiClient.get('/groups');
            setGroups(data || []);
        } catch (error) {
            console.error('Error loading groups:', error);
            alert('Failed to load groups. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const loadLocationCounts = async () => {
        try {
            const locations = await apiClient.get('/dealer-locations-with-addresses');
            const counts = {};
            (locations || []).forEach(location => {
                const groupId = location.group_id;
                if (groupId) {
                    counts[groupId] = (counts[groupId] || 0) + 1;
                }
            });
            setLocationCounts(counts);
        } catch (error) {
            console.error('Error loading location counts:', error);
        }
    };

    const handleAddGroup = async (formData) => {
        try {
            setSaving(true);
            await apiClient.post('/groups', {
                id: crypto.randomUUID(),
                name: formData.groupName
            });
            await loadGroups();
            await loadLocationCounts();
            setIsModalOpen(false);
        } catch (error) {
            console.error('Error creating group:', error);
            alert(`Failed to create group: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteGroup = async (group) => {
        const locationCount = locationCounts[group.id] || 0;

        let confirmMessage = `Are you sure you want to delete the group "${group.name}"?`;

        if (locationCount > 0) {
            confirmMessage = `Are you sure you want to delete the group "${group.name}"?\n\nThis will also permanently delete ${locationCount} location(s) assigned to this group.\n\nThis action cannot be undone.`;
        }

        if (!window.confirm(confirmMessage)) {
            return;
        }

        try {
            setSaving(true);
            const result = await apiClient.delete(`/groups/${group.id}`);
            await loadGroups();
            await loadLocationCounts();
            if (result?.deletedLocations > 0) {
                alert(`Group "${group.name}" deleted successfully.\n${result.deletedLocations} associated location(s) were also deleted.`);
            }
        } catch (error) {
            console.error('Error deleting group:', error);
            alert(`Failed to delete group: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleDeactivateGroup = async (group) => {
        if (!window.confirm(`Are you sure you want to deactivate the group "${group.name}"?`)) {
            return;
        }

        try {
            setSaving(true);
            await apiClient.put(`/groups/${group.id}`, { is_active: false });
            await loadGroups();
            await loadLocationCounts();
            alert(`Group "${group.name}" has been deactivated.`);
        } catch (error) {
            console.error('Error deactivating group:', error);
            alert(`Failed to deactivate group: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const startEditing = (group) => {
        setEditingId(group.id);
        setEditedGroup({ ...group });
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditedGroup(null);
    };

    const saveEditing = async () => {
        try {
            setSaving(true);
            await apiClient.put(`/groups/${editedGroup.id}`, {
                name: editedGroup.name,
                oem_id: editedGroup.oem_id || null
            });
            await loadGroups();
            cancelEditing();
        } catch (error) {
            console.error('Error updating group:', error);
            alert(`Failed to update group: ${error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setEditedGroup(prev => ({ ...prev, [name]: value }));
    };

    // Location management functions
    const fetchLocations = async (groupId) => {
        try {
            const allLocations = await apiClient.get('/dealer-locations-with-addresses');
            // Filter locations by group_id
            const groupLocations = (allLocations || [])
                .filter(loc => loc.group_id === groupId)
                .map(loc => ({
                    id: loc.id,
                    location_name: loc.name,
                    trading_name: loc.trading_name,
                    address: loc.street_name 
                        ? `${loc.building_number || ''} ${loc.street_name || ''} ${loc.locality || ''} ${loc.town_city || ''} ${loc.postcode || ''}`.trim()
                        : '',
                    group_id: loc.group_id,
                    address_id: loc.address_id,
                    brands: loc.brands || [], // Include brands array from API
                }));

            setLocations(groupLocations);
        } catch (err) {
            console.error('Failed to load locations:', err);
            setLocations([]);
        }
    };

    const toggleGroupExpanded = async (groupId) => {
        if (expandedGroupId === groupId) {
            setExpandedGroupId(null);
            setLocations([]);
        } else {
            setExpandedGroupId(groupId);
            await fetchLocations(groupId);
        }
    };

    const handleAddLocation = (groupId) => {
        setEditingLocation(null);
        setLocationFormData({
            location_name: '',
            address: '',
        });
        setSelectedBrandIds([]);
        setExpandedGroupId(groupId);
        setShowLocationModal(true);
        setError('');
    };

    const handleEditLocation = async (location) => {
        setEditingLocation(location);
        setLocationFormData({
            location_name: location.location_name || location.name || '',
            address: location.address || '',
        });
        setSelectedBrandIds([]);
        
        // Fetch existing brand associations for this location
        try {
            const allLocationBrands = await apiClient.get('/dealer-locations/brands');
            const locationBrands = (allLocationBrands || [])
                .filter(lb => lb.dealer_location_id === location.id)
                .map(lb => {
                    const brand = availableBrands.find(b => b.name === lb.brand_name);
                    return brand ? brand.id : null;
                })
                .filter(Boolean);
            setSelectedBrandIds(locationBrands);
        } catch (err) {
            console.error('Error fetching location brands:', err);
            setSelectedBrandIds([]);
        }
        
        setShowLocationModal(true);
        setError('');
    };

    const handleLocationSubmit = async (e) => {
        e.preventDefault();
        setError('');

        try {
            let addressId = editingLocation?.address_id || null;
            
            const locationData = {
                name: locationFormData.location_name,
                trading_name: locationFormData.location_name,
                group_id: expandedGroupId,
                address_id: addressId,
            };

            const endpoint = editingLocation
                ? `/dealer-locations/${editingLocation.id}`
                : '/dealer-locations';

            const savedLocation = editingLocation
                ? await apiClient.put(endpoint, locationData)
                : await apiClient.post(endpoint, locationData);
            // Use UUID directly - don't parse as integer
            const locationId = savedLocation?.id || editingLocation?.id;

            // Update brand associations
            if (locationId) {
                // Delete all existing associations
                try {
                    await apiClient.delete(`/dealer-locations/${locationId}/brands`);
                } catch (err) {
                    console.warn('Error deleting brand associations:', err);
                }

                // Create new associations if any are selected
                if (selectedBrandIds.length > 0) {
                    for (const brandId of selectedBrandIds) {
                        try {
                            await apiClient.post(`/dealer-locations/${locationId}/brands`, { brand_id: brandId });
                        } catch (err) {
                            console.error('Error associating brand:', err);
                        }
                    }
                }
            }

            if (expandedGroupId) {
                await fetchLocations(expandedGroupId);
            }
            await loadLocationCounts();
            handleCloseLocationModal();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteLocation = async (locationId) => {
        if (!window.confirm('Are you sure you want to delete this location?')) return;

        try {
            await apiClient.delete(`/dealer-locations/${locationId}`);

            if (expandedGroupId) {
                await fetchLocations(expandedGroupId);
            }
            await loadLocationCounts();
        } catch (err) {
            setError(err.message || 'Failed to delete location');
            console.error(err);
        }
    };

    const handleDeactivateLocation = async (locationId) => {
        if (!window.confirm('Are you sure you want to deactivate this location?')) return;

        try {
            await apiClient.put(`/dealer-locations/${locationId}`, { is_active: false });

            if (expandedGroupId) {
                await fetchLocations(expandedGroupId);
            }
            await loadLocationCounts();
            alert('Location has been deactivated.');
        } catch (err) {
            setError(err.message || 'Failed to deactivate location');
            console.error(err);
        }
    };

    const handleRemoveBrandFromLocation = async (locationId, brandId) => {
        try {
            // Ensure brandId is an integer
            const brandIdInt = parseInt(brandId, 10);
            if (isNaN(brandIdInt)) {
                throw new Error('Invalid brand ID');
            }

            await apiClient.delete(`/dealer-locations/${locationId}/brands?brand_id=${brandIdInt}`);

            // Refresh locations to show updated brand list
            if (expandedGroupId) {
                await fetchLocations(expandedGroupId);
            }
        } catch (err) {
            setError(err.message || 'Failed to remove brand from location');
            console.error('Error removing brand:', err);
            alert(err.message || 'Failed to remove brand from location');
        }
    };

    const handleCloseLocationModal = () => {
        setShowLocationModal(false);
        setEditingLocation(null);
        setLocationFormData({
            location_name: '',
            address: '',
        });
        setSelectedBrandIds([]);
        setError('');
    };

    const modalFields = [
        { name: "Group Name", id: "groupName", type: "text" }
    ];

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-500">Loading groups...</div>
            </div>
        );
    }

    return (
        <div>
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Dealership Groups</h2>
                    <p className="text-gray-600 mt-1">
                        Manage dealership groups that organize locations
                    </p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-150"
                    disabled={saving}
                >
                    <Plus size={18} />
                    Add Group
                </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border">
                <div className="p-6">
                    {groups.length === 0 ? (
                        <div className="text-center py-12">
                            <Building2 size={48} className="mx-auto text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No groups found</h3>
                            <p className="text-gray-500 mb-4">Get started by creating your first dealership group.</p>
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-150"
                                disabled={saving}
                            >
                                <Plus size={16} />
                                Add Group
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-1">
                            <div className="grid grid-cols-4 gap-4 text-xs font-bold text-gray-500 uppercase p-3 border-b">
                                <span>Group Name</span>
                                <span>OEM</span>
                                <span>Locations</span>
                                <span className="text-right">Actions</span>
                            </div>
                            {groups.map(group => (
                                <React.Fragment key={group.id}>
                                    <div className="grid grid-cols-4 gap-4 items-center p-3 hover:bg-gray-50 rounded-lg border-b border-gray-100">
                                        {editingId === group.id ? (
                                            <>
                                                <div>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        value={editedGroup.name}
                                                        onChange={handleEditChange}
                                                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        disabled={saving}
                                                        placeholder="Enter group name"
                                                    />
                                                </div>
                                                <div>
                                                    <select
                                                        name="oem_id"
                                                        value={editedGroup.oem_id || ''}
                                                        onChange={handleEditChange}
                                                        className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                        disabled={saving}
                                                    >
                                                        <option value="">No OEM (Independent)</option>
                                                        {availableOEMs.filter(o => o.is_active).map(oem => (
                                                            <option key={oem.id} value={oem.id}>{oem.name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    {locationCounts[group.id] || 0} locations
                                                </div>
                                                <div className="flex justify-end items-center space-x-2">
                                                    <button
                                                        onClick={saveEditing}
                                                        className="p-1.5 text-green-600 hover:bg-green-100 rounded transition-colors duration-150"
                                                        disabled={saving}
                                                        title="Save changes"
                                                    >
                                                        <Save size={16} />
                                                    </button>
                                                    <button
                                                        onClick={cancelEditing}
                                                        className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors duration-150"
                                                        disabled={saving}
                                                        title="Cancel editing"
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div>
                                                    <h3 className="font-medium text-gray-900">{group.name}</h3>
                                                    <p className="text-xs text-gray-500 mt-1">ID: {group.id}</p>
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    {group.oem_name ? (
                                                        <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                                            {group.oem_name}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400 text-xs">Independent</span>
                                                    )}
                                                </div>
                                                <div className="text-sm text-gray-600">
                                                    <button
                                                        onClick={() => toggleGroupExpanded(group.id)}
                                                        className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 hover:bg-blue-200 cursor-pointer"
                                                    >
                                                        {expandedGroupId === group.id ? '▼' : '▶'} {locationCounts[group.id] || 0} {(locationCounts[group.id] || 0) === 1 ? 'location' : 'locations'}
                                                    </button>
                                                </div>
                                                <div className="flex justify-end items-center space-x-2">
                                                    <button
                                                        onClick={() => startEditing(group)}
                                                        className="p-1.5 text-gray-500 hover:bg-gray-100 rounded transition-colors duration-150"
                                                        disabled={saving}
                                                        title="Edit group"
                                                    >
                                                        <Edit size={16} />
                                                    </button>
                                                    {userRole === 'admin' ? (
                                                        <button
                                                            onClick={() => handleDeleteGroup(group)}
                                                            className="p-1.5 text-red-500 hover:bg-red-100 rounded transition-colors duration-150"
                                                            disabled={saving}
                                                            title="Delete group and all associated locations"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => handleDeactivateGroup(group)}
                                                            className="p-1.5 text-orange-500 hover:bg-orange-100 rounded transition-colors duration-150"
                                                            disabled={saving}
                                                            title="Deactivate group"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    {expandedGroupId === group.id && (
                                        <div className="bg-gray-50 p-4 border-b border-gray-100">
                                            <div className="space-y-3">
                                                <div className="flex justify-between items-center mb-3">
                                                    <h3 className="text-lg font-semibold text-gray-900">Locations for {group.name}</h3>
                                                    <button
                                                        onClick={() => handleAddLocation(group.id)}
                                                        className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                                                    >
                                                        + Add Location
                                                    </button>
                                                </div>
                                                {error && (
                                                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                                                        {error}
                                                    </div>
                                                )}
                                                {locations.length === 0 ? (
                                                    <div className="text-center py-8 text-gray-500">
                                                        No locations found. Click "Add Location" to create one.
                                                    </div>
                                                ) : (
                                                    <div className="bg-white rounded-lg border border-gray-200">
                                                        <table className="min-w-full divide-y divide-gray-200">
                                                            <thead className="bg-gray-100">
                                                                <tr>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Location Name</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Trading Name</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Brand/Franchise</th>
                                                                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-600">Address</th>
                                                                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-600">Actions</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody className="divide-y divide-gray-200">
                                                                {locations.map((location) => (
                                                                    <tr key={location.id} className="hover:bg-gray-50">
                                                                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{location.location_name}</td>
                                                                        <td className="px-4 py-3 text-sm text-gray-600">{location.trading_name || '-'}</td>
                                                                        <td className="px-4 py-3 text-sm">
                                                                            {location.brands && location.brands.length > 0 ? (
                                                                                <div className="flex flex-wrap gap-1">
                                                                                    {location.brands.map((brand, idx) => (
                                                                                        <span
                                                                                            key={brand.id || idx}
                                                                                            className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800 flex items-center gap-1"
                                                                                        >
                                                                                            {brand.name}
                                                                                            <button
                                                                                                onClick={() => handleRemoveBrandFromLocation(location.id, brand.id)}
                                                                                                className="ml-1 text-red-600 hover:text-red-800 focus:outline-none"
                                                                                                title="Remove brand from location"
                                                                                            >
                                                                                                <XCircle size={12} />
                                                                                            </button>
                                                                                        </span>
                                                                                    ))}
                                                                                </div>
                                                                            ) : (
                                                                                <span className="text-gray-400">-</span>
                                                                            )}
                                                                        </td>
                                                                        <td className="px-4 py-3 text-sm text-gray-600">{location.address || '-'}</td>
                                                                        <td className="px-4 py-3 text-sm text-right">
                                                                            <button
                                                                                onClick={() => handleEditLocation(location)}
                                                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                                                            >
                                                                                Edit
                                                                            </button>
                                                                            {userRole === 'admin' ? (
                                                                                <button
                                                                                    onClick={() => handleDeleteLocation(location.id)}
                                                                                    className="text-red-600 hover:text-red-900"
                                                                                >
                                                                                    Delete
                                                                                </button>
                                                                            ) : (
                                                                                <button
                                                                                    onClick={() => handleDeactivateLocation(location.id)}
                                                                                    className="text-orange-600 hover:text-orange-900"
                                                                                >
                                                                                    Deactivate
                                                                                </button>
                                                                            )}
                                                                        </td>
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {saving && (
                <div className="mt-4 text-sm text-blue-600 text-center">
                    Processing changes...
                </div>
            )}

            <AddItemModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSave={handleAddGroup}
                title="Add New Dealership Group"
                fields={modalFields}
            />

            {/* Location Modal */}
            {showLocationModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h2 className="text-2xl font-bold mb-6">
                                {editingLocation ? 'Edit Location' : 'Add New Location'}
                            </h2>

                            {error && (
                                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                                    {error}
                                </div>
                            )}

                            <form onSubmit={handleLocationSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Location Name *
                                    </label>
                                    <input
                                        type="text"
                                        required
                                        value={locationFormData.location_name}
                                        onChange={(e) =>
                                            setLocationFormData({ ...locationFormData, location_name: e.target.value })
                                        }
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter location name"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Address (Optional)
                                    </label>
                                    <textarea
                                        value={locationFormData.address}
                                        onChange={(e) =>
                                            setLocationFormData({ ...locationFormData, address: e.target.value })
                                        }
                                        rows={2}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                        placeholder="Enter address details"
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Note: Full address management is available in the Location Configuration page</p>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Brand/Franchise (Optional)
                                    </label>
                                    <div className="border border-gray-300 rounded-lg p-3 max-h-48 overflow-y-auto bg-white">
                                        {availableBrands.length === 0 ? (
                                            <p className="text-sm text-gray-500">No brands available. Add brands in the Admin section.</p>
                                        ) : (
                                            <div className="space-y-2">
                                                {availableBrands.map(brand => (
                                                    <label key={brand.id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedBrandIds.includes(brand.id)}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedBrandIds([...selectedBrandIds, brand.id]);
                                                                } else {
                                                                    setSelectedBrandIds(selectedBrandIds.filter(id => id !== brand.id));
                                                                }
                                                            }}
                                                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                        />
                                                        <span className="text-sm text-gray-700">{brand.name}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-end gap-4 mt-6">
                                    <button
                                        type="button"
                                        onClick={handleCloseLocationModal}
                                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                                    >
                                        {editingLocation ? 'Update Location' : 'Create Location'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminGroupsPage;